import React, { useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { performDatabaseAction } from '../../../api/systemApi';

const AdminActions = ({ onActionSuccess }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [actionType, setActionType] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [error, setError] = useState('');

    const handleAction = async (action) => {
        if (action === 'clear_cache') {
            setActionType('clear_cache');
            setShowConfirmModal(true);
            return;
        }
        
        // For other actions that might be added in the future
        await executeAction(action);
    };

    const executeAction = async (action) => {
        setIsLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const result = await performDatabaseAction(action);
            
            if (result.success) {
                setSuccessMessage(result.message);
                onActionSuccess();
                
                // Show success message for 5 seconds
                setTimeout(() => {
                    setSuccessMessage('');
                }, 5000);
            } else {
                setError(result.message || 'Action failed');
            }
        } catch (err) {
            console.error('Database action failed:', err);
            setError('Failed to perform action. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const confirmAction = async () => {
        setShowConfirmModal(false);
        await executeAction(actionType);
    };

    const cancelAction = () => {
        setShowConfirmModal(false);
        setActionType('');
    };

    return (
        <div className="button-container p-4 rounded">
            <h4 className="font-medium mb-3 text-primary">Administrative Actions</h4>
            
            {/* Success Message */}
            {successMessage && (
                <div className="mb-4 p-3 bg-green-900 bg-opacity-20 border border-green-500 rounded-lg">
                    <div className="flex items-center gap-2">
                        <Icon icon="mdi:check-circle" className="text-green-500" />
                        <span className="text-green-300 text-sm font-medium">Success</span>
                    </div>
                    <p className="text-green-200 text-xs mt-1">{successMessage}</p>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-900 bg-opacity-20 border border-red-500 rounded-lg">
                    <div className="flex items-center gap-2">
                        <Icon icon="mdi:alert-circle" className="text-red-500" />
                        <span className="text-red-300 text-sm font-medium">Error</span>
                    </div>
                    <p className="text-red-200 text-xs mt-1">{error}</p>
                </div>
            )}

            <div className="flex flex-col md:flex-row gap-2 md:gap-3">
                {/* Clear Application Cache */}
                <button
                    onClick={() => handleAction('clear_cache')}
                    disabled={isLoading}
                    className="w-full px-4 py-3 bg-red-500 text-white rounded text-sm hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2 text-base"
                >
                    {isLoading ? (
                        <Icon icon="eos-icons:loading" className=" animate-spin text-xl" />
                    ) : (
                        <Icon icon="mdi:delete-sweep" className="text-xl" />
                    )}
                    Clear Application Cache
                </button>

                {/* Run Database Migrations - Disabled */}
                <div className="w-full">
                    <button
                        disabled={true}
                        className="w-full px-4 py-3 bg-gray-500 text-white rounded text-sm cursor-not-allowed flex items-center justify-center gap-2 opacity-50 text-base"
                        title="This feature is planned for a future release"
                    >
                        <Icon icon="mdi:database-sync" className="text-xl" />
                        Run Pending Database Migrations
                    </button>
                    <div className="absolute -top-8 left-0 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                        This feature is planned for a future release
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-bgSecondary shadow-neo-card p-6 rounded-lg max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <Icon icon="mdi:alert-circle" className="text-yellow-500 text-2xl" />
                            <h3 className="text-lg font-medium text-font-primary">Confirm Action</h3>
                        </div>
                        
                        <p className="text-font-secondary mb-6">
                            Are you sure you want to clear the server-side cache? This will clear the cache for Auth0 roles, organizations, and members.
                        </p>
                        
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={cancelAction}
                                className="px-4 py-2 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAction}
                                disabled={isLoading}
                                className="px-4 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                {isLoading ? (
                                    <Icon icon="eos-icons:loading" className="animate-spin" />
                                ) : (
                                    <Icon icon="mdi:delete-sweep" />
                                )}
                                Clear Cache
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminActions;
